import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portalDrill } from '../../api/portal';
import { downloadCsv } from '../shared/exportData';
import GenericCard from '../shared/GenericCard';

/**
 * HistoryPanel — the full record (Epic 8 · S8.3, Part A.3).
 *
 * search-console/PaginatedTable.js columns: Title · Park (multi-park) · Stage
 * (stage_label) · Asked (asked_label) · Done (when.label when kind done, else
 * "—"). Server-side paging: Previous/Next driven from total/offset. Search
 * box with 300ms debounce → `portalDrill('history', null, {park_id, q,
 * offset:0}, limit)`. "Show all" when total > 20 → ListDetail (`onShowAll`).
 * Export CSV: fetchAll loop offset += 200 until ≥ total, exporting the same
 * labels.
 *
 * 0 total → `insufficient` → GenericCard (title + sentence, no table).
 *
 * Self-contained — owns its `portalDrill('history')` data flow (like
 * ConversationPanel owns `olderMessages`). Never imports `supabase` directly.
 */
const LIMIT = 20;
const DEBOUNCE_MS = 300;
const CSV_PAGE = 200;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const doneCell = (row) => {
  if (row?.when?.label) return row.when.label;
  if (row?.done_at) {
    const d = new Date(row.done_at);
    if (!isNaN(d.getTime())) return `Done ${MONTHS[d.getMonth()]} ${d.getDate()}`;
    return String(row.done_at);
  }
  return '—';
};

// CSV column spec — same labels as the table, raw values for the spreadsheet.
const CSV_COLUMNS = [
  { header: 'Title', get: (r) => r?.title ?? '' },
  { header: 'Park', get: (r) => r?.park ?? '' },
  { header: 'Stage', get: (r) => r?.stage_label ?? '' },
  { header: 'Asked', get: (r) => r?.asked_label ?? '—' },
  { header: 'Done', get: (r) => doneCell(r) },
];

const HistoryPanel = ({ facts, parkId, multiPark = false, onShowAll, onOpenItem }) => {
  // Initialise from the page payload — fn_get_portal already delivers the newest
  // 20 rows in facts.rows (Part A.3). Using them avoids a redundant drill fetch
  // on mount and makes the table populate immediately on page load.
  const [rows, setRows] = useState(() => Array.isArray(facts?.rows) ? facts.rows : []);
  const [total, setTotal] = useState(typeof facts?.total === 'number' ? facts.total : 0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const reqId = useRef(0);
  const debounceRef = useRef(null);

  const totalFromFacts = typeof facts?.total === 'number' ? facts.total : 0;

  const fetchPage = useCallback(async (query, off) => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await portalDrill('history', null, { park_id: parkId, q: query || undefined, offset: off }, LIMIT);
      if (myReq !== reqId.current) return;
      const data = res?.data ?? {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data.rows) ? data.rows.length : 0));
      setOffset(typeof data.offset === 'number' ? data.offset : off);
    } catch (e) {
      if (myReq !== reqId.current) return;
      setError(e?.message || "Couldn't load history.");
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [parkId]);

  // Debounced search — 300ms. Empty query = reset.
  const onSearchChange = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPage(val, 0), DEBOUNCE_MS);
  };

  const prev = () => { const o = Math.max(0, offset - LIMIT); fetchPage(q, o); };
  const next = () => { if (offset + LIMIT < total) fetchPage(q, offset + LIMIT); };
  const hasPrev = offset > 0;
  const hasNext = offset + LIMIT < total;

  // Export CSV — fetchAll loop: offset += CSV_PAGE until ≥ total.
  const exportCsv = async () => {
    setExporting(true);
    try {
      let off = 0;
      const allRows = [];
      while (true) {
        const res = await portalDrill('history', null, { park_id: parkId, q: q || undefined, offset: off }, CSV_PAGE);
        const data = res?.data ?? {};
        const pageRows = Array.isArray(data.rows) ? data.rows : [];
        allRows.push(...pageRows);
        const t = typeof data.total === 'number' ? data.total : allRows.length;
        off += CSV_PAGE;
        if (off >= t || pageRows.length === 0) break;
      }
      const headers = CSV_COLUMNS.map((c) => c.header);
      const body = allRows.map((r) => CSV_COLUMNS.map((c) => c.get(r)));
      downloadCsv(headers, body, 'MY WORK HISTORY.csv');
    } catch (e) {
      setError(e?.message || "Couldn't export.");
    } finally {
      setExporting(false);
    }
  };

  const showAll = totalFromFacts > 20;

  // Initial load + refetch on park change.
  // — Skip entirely for empty parks (total 0): no fetch, no table (Part G).
  // — If the page payload already provided rows (facts.rows), use them
  //   directly instead of making a redundant drill call. Only fall back to
  //   portalDrill when facts.rows is absent (e.g. unit tests that pass just
  //   { total }).
  useEffect(() => {
    if (totalFromFacts === 0) {
      setRows([]);
      return;
    }
    if (Array.isArray(facts?.rows) && facts.rows.length > 0) {
      setRows(facts.rows);
      setTotal(typeof facts.total === 'number' ? facts.total : facts.rows.length);
      setOffset(0);
      return;
    }
    fetchPage('', 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, totalFromFacts, facts]);

  // 0 total → GenericCard (insufficient — no table). This MUST sit after all
  // hooks (useState/useCallback/useEffect above) to satisfy React's rules of
  // hooks — a conditional return before a hook throws when the condition
  // flips between renders (e.g. switching from an empty park to one with
  // history).
  if (totalFromFacts === 0 && rows.length === 0 && !loading) {
    return (
      <section data-testid="panel-history">
        <GenericCard title="History" sentence="Not enough history to show yet." />
      </section>
    );
  }

  return (
    <section data-testid="panel-history" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">History</h3>
        <div className="flex items-center gap-3">
          <button type="button" onClick={exportCsv} disabled={exporting || total === 0}
            data-testid="history-export-csv"
            className="text-sm font-medium text-[#32B1FF] hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {showAll && (
            <button type="button" onClick={onShowAll} data-testid="history-show-all"
              className="text-sm font-medium text-[#32B1FF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded">
              Show all
            </button>
          )}
        </div>
      </div>

      <input type="text" value={q} onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search history…" aria-label="Search history" data-testid="history-search"
        className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]" />

      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">{error}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-3 font-medium">Title</th>
              {multiPark && <th className="py-2 px-3 font-medium">Park</th>}
              <th className="py-2 px-3 font-medium">Stage</th>
              <th className="py-2 px-3 font-medium">Asked</th>
              <th className="py-2 px-3 font-medium">Done</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={multiPark ? 5 : 4} className="py-4 text-gray-400 dark:text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={multiPark ? 5 : 4} className="py-4 text-gray-500 dark:text-gray-400">No items found.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={() => onOpenItem?.(r)} data-testid="history-row">
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">{r.title}</td>
                  {multiPark && <td className="py-2 px-3 text-gray-600 dark:text-gray-300 truncate">{r.park || '—'}</td>}
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.stage_label || '—'}</td>
                  <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{r.asked_label || '—'}</td>
                  <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{doneCell(r)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={prev} disabled={!hasPrev || loading} data-testid="history-prev"
              className="text-sm font-medium text-[#32B1FF] hover:underline disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
            <button type="button" onClick={next} disabled={!hasNext || loading} data-testid="history-next"
              className="text-sm font-medium text-[#32B1FF] hover:underline disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HistoryPanel;
